import React, { lazy, Suspense, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { loadComponentModule } from "components/ComponentType";
import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { ProgressSpinner } from "primereact/progressspinner";
import ErrorBoundary from "../error-boundary/ErrorBoundary";
import { adjustReferenceTime } from "../../utils";
import { ComponentProvider, useComponentStoreContext } from "./ComponentProvider";

interface ComponentContextWrapperProps {
  component: IDashboardComponent;
  tabId: number;
  groupId: string;
  isEditMode: boolean;
}

const ComponentContextWrapperInner = ({
  component,
  tabId,
  groupId,
  isEditMode,
}: ComponentContextWrapperProps) => {
  const getTabContextConfig = useDashboardStore(state => state.getTabContextConfig);
  const refreshKey = useDashboardStore(state => state.refreshKey);

  const store = useComponentStoreContext();

  const { setComponentId } = store.getState();
  const { setGroupId } = store.getState();

  const tabContextConfig = getTabContextConfig(tabId) || {};

  const {
    referenceTime,
    dateFrom: from,
    dateTo: to,
  } = adjustReferenceTime(tabContextConfig.config);

  const mergedConfig = {
    ...component.config,
    hideHeader: component.hideHeader,
    referenceTime: tabContextConfig.referenceTime || referenceTime,
    from: tabContextConfig.from || from,
    to: tabContextConfig.to || to,
  };

  const LazyComponent = useMemo(() => {
    return lazy(async () => {
      const loadedModule = await loadComponentModule(component.type);
      return { default: loadedModule.default };
    });
  }, [component.type]);

  const { ref, inView } = useInView({
    triggerOnce: isEditMode,
    rootMargin: `1000px 0px`,
  });

  setComponentId(component.id);
  setGroupId(groupId);

  const shouldLoad = component.config?.reportMode || inView;

  return (
    <ErrorBoundary
      errorMessage={
        component.type === "NOT_SUPPORTED" ? T("error.component-is-not-supported") : undefined
      }
    >
      <div ref={ref} className="h-full">
        {shouldLoad && (
          <Suspense fallback={<ProgressSpinner />}>
            <LazyComponent key={refreshKey} {...mergedConfig} />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};

const ComponentContextWrapper = React.memo(ComponentContextWrapperInner);

export default function WrappedComponentContextWrapper(props: ComponentContextWrapperProps) {
  return (
    <ComponentProvider initialState={{ componentId: props.component.id, groupId: props.groupId }}>
      <ComponentContextWrapper {...props} />
    </ComponentProvider>
  );
}
